import * as React from "react";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import {
  additionalRounds,
  defaultRounds,
  firstRound,
  lastRound,
} from "../util/halvesLogic";
import { Round } from "../types/halves";
import { Box, styled } from "@mui/material";
import { DART_NUMBER } from "../types/dart";

function not(a: readonly Round[], b: readonly Round[]) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a: readonly Round[], b: readonly Round[]) {
  return a.filter((value) => b.indexOf(value) !== -1);
}

export default function HalvesRoundList() {
  const [checked, setChecked] = React.useState<Round[]>([]);
  const [left, setLeft] = React.useState<Round[]>(additionalRounds);
  const [right, setRight] = React.useState<Round[]>(defaultRounds);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value: Round) => () => {
    if (value === lastRound || value === firstRound) return;
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    setRight(right.concat(left));
    setLeft([]);
  };

  const handleCheckedRight = () => {
    const newRight = right.splice(-1);
    if (!newRight) return;
    for (const round of leftChecked) {
      newRight.push(round);
    }
    newRight.push(lastRound);
    setRight(newRight);
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    const remove = right.filter((r) => r !== lastRound && r !== firstRound);
    setLeft(left.concat(remove));
    setRight([firstRound, lastRound]);
  };
  const customList = (items: readonly Round[]) => (
    <Paper sx={{ width: 200, height: 230, overflow: "auto" }}>
      <List dense component="div" role="list">
        {items.map((value: Round) => {
          const labelId = `transfer-list-item-${
            value.number
              ? `${value.number}s`
              : value.color
              ? `${value.color}`
              : value.type
          }-label`;

          return (
            <ListItem
              key={
                value.number
                  ? `${value.number}s`
                  : value.color
                  ? `${value.color}`
                  : value.type
              }
              role="listitem"
              button
              onClick={handleToggle(value)}
            >
              <ListItemIcon>
                <Checkbox
                  disabled={value === firstRound || value === lastRound}
                  checked={checked.indexOf(value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    "aria-labelledby": labelId,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={
                  value.number && value.number !== DART_NUMBER.BULL
                    ? `${value.number}s`
                    : value.color
                    ? `${value.color}`
                    : value.type
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );

  return (
    <Wrapper>
      <Grid
        style={{ width: "100%" }}
        container
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>{customList(left)}</Grid>
        <Grid item>
          <Grid container direction="column" alignItems="center">
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleAllRight}
              disabled={left.length === 0}
              aria-label="move all right"
            >
              ≫
            </Button>
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="move selected right"
            >
              &gt;
            </Button>
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
            >
              &lt;
            </Button>
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleAllLeft}
              disabled={right.length === 0}
              aria-label="move all left"
            >
              ≪
            </Button>
          </Grid>
        </Grid>
        <Grid item>{customList(right)}</Grid>
      </Grid>
    </Wrapper>
  );
}

const Wrapper = styled(Box)({
  width: "100%",
});
